import {
    AgentWalletTransactions,
    CashBackSummaryModel,
    ExpandedWalletTansaction,
    FundTransferOutlet,
    TransactionPagination,
    WalletBalanceData,
    WalletFundingBankAccountData,
    WalletSummaryModel,
    WalletTransactionDetail,
    WalletTransactionParamsModel,
    WalletTransactionRefModel,
    WalletTransferRequestModel,
} from '../../shared/models/wallet.model';
import { StringHelper } from '../helpers/strings.helper';
import { AppConfig } from '../../shared/config/app-config';
import { Injectable } from '@angular/core';
import { ApiClientHelper } from '../helpers/api-client.helper';
import { Subject } from 'rxjs';
import { LocalStorageService } from './local-storage.service';
import { AuthenticationService } from './authentication.service';
import { AccountStatementResponse } from '../models/account-statement.model';
import { DepositConfigModel } from '../../shared/models/deposit.model';

@Injectable({
    providedIn: 'root',
})
export class WalletService {
    private _transactionfilter = new Subject<WalletTransactionParamsModel>();
    _transactionfilter$ = this._transactionfilter.asObservable();

    private _transactionfilterresult = new Subject<AgentWalletTransactions>();
    _transactionfilterresult$ = this._transactionfilterresult.asObservable();
    private currentContext = 'wallet';
    private isCashbackEnabled: boolean;
    private walletTotalPages: number;
    private cashBackTotalPages: number;

    constructor(
        private apiClientHelper: ApiClientHelper,
        public config: AppConfig,
        private authService: AuthenticationService,
        private localStorage: LocalStorageService,
    ) {
        const isCashbackEnabled = this.localStorage.get('isCashbackEnabled');
        let page = this.localStorage.get('wallet-total-pages');
        if (page) {
            page = JSON.parse(page);
            this.walletTotalPages = page?.total;
        }
        let cashbackPage = this.localStorage.get('cashback-total-pages');
        if (cashbackPage) {
            cashbackPage = JSON.parse(cashbackPage);
            this.cashBackTotalPages = cashbackPage?.total;
        }
        this.isCashbackEnabled = isCashbackEnabled === '11';
    }

    private _context = new Subject<string>();

    _context$ = this._context.asObservable();

    get context() {
        const context = this.authService.getContextUser();
        return context;
    }

    getWalletSummary(params?, rm = false): Promise<WalletSummaryModel> {
        params = StringHelper.toUrlString(params);
        let url = this.config.MONIEPOINT_HOST + `/api/v1/wallets/summary?${params}`;
        if (this.context === 'area-manager' && !rm) {
            url = this.config.MONIEPOINT_HOST + `/api/v1/area-manager/account/summary?${params}`;
        }
        return this.apiClientHelper.getForPromise(url);
    }

    getWalletSummaryV3(params?, rm = false): Promise<WalletSummaryModel> {
        params = StringHelper.toUrlString(params);
        let url = this.config.MONIEPOINT_HOST + `/api/v3/wallets/summary?${params}`;
        if (this.context === 'area-manager' && !rm) {
            url = this.config.MONIEPOINT_HOST + `/api/v1/area-manager/account/summary?${params}`;
        }
        return this.apiClientHelper.getForPromise(url);
    }

    getWalletSummaryV2(params?, rm = false): Promise<WalletSummaryModel> {
        params = StringHelper.toUrlString(params);
        let url = this.config.MONIEPOINT_HOST + `/api/v2/wallets/summary?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }

    getWalletTrasactions(params): Promise<WalletSummaryModel> {
        params = StringHelper.toUrlString(params);
        let url = this.config.MONIEPOINT_HOST + `/api/v2/wallets/transactions?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }

    getExpandedWalletTransaction(params): Promise<TransactionPagination<ExpandedWalletTansaction[]>> {
        params = StringHelper.toUrlString(params);
        let url = this.config.MONIEPOINT_HOST + `/api/v2/wallets/transactions/expanded?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }

    setWalletTotalPages(total: number) {
        const page = {
            total,
        };
        this.localStorage.save('wallet-total-pages', JSON.stringify(page));
        this.walletTotalPages = total;
    }

    getWalletTotalPages() {
        return this.walletTotalPages;
    }

    setCashBackTotalPages(total: number) {
        const page = {
            total,
        };
        this.localStorage.save('cashback-total-pages', JSON.stringify(page));
        this.cashBackTotalPages = total;
    }

    getCashBackTotalPages() {
        return this.cashBackTotalPages;
    }

    setCashbackEnabled(isCashback: boolean) {
        this.localStorage.save('isCashbackEnabled', isCashback ? '11' : '00');
        this.isCashbackEnabled = isCashback;
    }

    getCashbackEnabled() {
        return this.isCashbackEnabled;
    }

    getCashSummary(params): Promise<CashBackSummaryModel> {
        params = StringHelper.toUrlString(params);
        const url = this.config.MONIEPOINT_HOST + `/api/v1/cash-back/summary?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }

    setWalletTransactionFilter(params: WalletTransactionParamsModel) {
        this._transactionfilter.next(params);
    }

    setCurrentContext(context: string) {
        this.currentContext = context;
        this._context.next(context);
    }

    getCurrentContext() {
        return this.currentContext;
    }

    setWalletTransactionFilterResult(result: AgentWalletTransactions) {
        this._transactionfilterresult.next(result);
    }

    getWalletTransactions(params): Promise<AgentWalletTransactions> {
        const context = this.authService.getContextUser();
        let agentParams = params;
        params = StringHelper.toUrlString(params);
        let url = this.config.MONIEPOINT_HOST + `/api/v1/wallets/agent-manager/transactions/search?${params}`;
        if (context === 'agents') {
            agentParams.transactionDate = agentParams.startDate;
            agentParams = StringHelper.toUrlString(agentParams);
            url = this.config.MONIEPOINT_HOST + `/api/v1/wallets/transactions?${agentParams}`;
        } else if (context === 'area-manager') {
            url = this.config.MONIEPOINT_HOST + `/api/v1/area-manager/account/transactions?${agentParams}`;
        }
        return this.apiClientHelper.getForPromise(url);
    }

    getTransactionByDate(params, isRM = false): Promise<WalletTransactionDetail> {
        params = StringHelper.toUrlString(params);
        let url;
        url = this.config.MONIEPOINT_HOST + `/api/v1/wallets/transactions?${params}`;
        if (this.context === 'area-manager' && !isRM) {
            url = this.config.MONIEPOINT_HOST + `/api/v1/area-manager/account/transactions?${params}`;
        }
        return this.apiClientHelper.getForPromise(url);
    }

    getCashBackTransactionByDate(params): Promise<WalletTransactionDetail> {
        params = StringHelper.toUrlString(params);
        const url = this.config.MONIEPOINT_HOST + `/api/v1/cash-back/transactions?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }

    getTransactionDetails(params): Promise<WalletTransactionRefModel> {
        params = StringHelper.toUrlString(params);
        const url = this.config.MONIEPOINT_HOST + `/api/v1/transactions?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }

    getConsolidatedTransactions(params): Promise<any> {
        params = StringHelper.toUrlString(params);
        const url = this.config.MONIEPOINT_HOST + `/api/v3/agent-consolidated-transactions?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }

    getWalletBalance(useInternalBalance = false): Promise<WalletBalanceData> {
        const params = StringHelper.toUrlString({ internal: useInternalBalance });
        const url = this.config.MONIEPOINT_HOST + `/api/v1/wallets/balances?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }

    getCashBackBalance(): Promise<any> {
        const url = this.config.MONIEPOINT_HOST + '/api/v1/cash-back/balance';
        return this.apiClientHelper.getForPromise(url);
    }

    getLinkedFundingAccount(): Promise<WalletFundingBankAccountData> {
        const url = `${this.config.MONIEPOINT_HOST}/api/v1/wallets/linked-funding-account`;
        return this.apiClientHelper.getForPromise(url, true);
    }

    getFundTransferOutlets(): Promise<FundTransferOutlet[]> {
        const url = this.config.MONIEPOINT_HOST + `/api/v1/agent-managers/fund-transfer/outlets`;
        return this.apiClientHelper.getForPromise(url);
    }

    exportTransactions(params): Promise<any> {
        params = StringHelper.toUrlString(params);
        const url = this.config.MONIEPOINT_HOST + `/api/v1/wallets/transactions/export?${params}`;
        return this.apiClientHelper.getAsBlobForPromise(url);
    }

    fundWallet(params: WalletTransferRequestModel) {
        const url = this.config.MONIEPOINT_HOST + `/api/v1/agent-managers/agent-outlets/fund-transfer`;
        return this.apiClientHelper.postForPromise(url, params);
    }

    downloadHistoryFile(params): Promise<any> {
        params = StringHelper.toUrlString(params);
        const url = this.config.MONIEPOINT_REPORT + `/api/v1/historical-transactions/download?${params}`;
        return this.apiClientHelper.getAsBlobForPromise(url);
    }

    downloadHistoryFileBO(params): Promise<any> {
        params = StringHelper.toUrlString(params);
        const url = this.config.MONIEPOINT_REPORT + `/api/v1/historical-transactions/business-owners/download?${params}`;
        return this.apiClientHelper.getAsBlobForPromise(url);
    }

    downloadBusinessOwnerDailySummaryFile(params): Promise<any> {
        params = StringHelper.toUrlString(params);
        const url = this.config.MONIEPOINT_REPORT + `/api/v1/historical-transactions/business-owners/daily-summary/download?${params}`;
        return this.apiClientHelper.getAsBlobForPromise(url);
    }

    downloadBusinessOwnerDailyTerminalSummaryFile(params): Promise<any> {
        params = StringHelper.toUrlString(params);
        const url = this.config.MONIEPOINT_REPORT + `/api/v1/historical-transactions/business-owners/daily-terminal-summary/download?${params}`;
        return this.apiClientHelper.getAsBlobForPromise(url);
    }

    getBusinessOwnerDailySummary(params): Promise<AccountStatementResponse> {
        params = StringHelper.toUrlString(params);
        const version = this.config.USE_DAILY_STATEMENT_DATE_RANGE ? 'v3' : 'v2';
        const baseUrl = this.config.USE_STATEMENT_REPORT_URL ? this.config.MONIEPOINT_STATEMENT : this.config.MONIEPOINT_REPORT;
        const url = baseUrl + `/api/${version}/historical-transactions/download?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }

    getDepositConfig(): Promise<DepositConfigModel> {
        const url = this.config.MONIEPOINT_HOST + `/api/v1/configurations/deposit`;
        return this.apiClientHelper.getForPromise(url);
    }

    getAccountInfo(): Promise<any> {
        const url = this.config.MONIEPOINT_HOST + '/api/v1/wallets/balances';
        return this.apiClientHelper.getForPromise(url);
    }

    getLienAccountDetail(id, params): Promise<any> {
        params = StringHelper.toUrlString(params);
        const url = this.config.MONIEPOINT_HOST + `/api/v2/businesses/active-lien?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }

    getPndDetails(): Promise<any> {
        const url = this.config.MONIEPOINT_ACCOUNTS + `/api/v1/wallets/pnd-details`;
        return this.apiClientHelper.getForPromise(url);
    }
}
